import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

/** Parses `location.search` into URLSearchParams. */
export function useUrlSearchParams() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

type SetSearchParams = (next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void;

/**
 * Same call shape as React Router 6 `useSearchParams`.
 * This app is on react-router-dom 5.2, which does not export that hook.
 * Writes use `history.replace`, matching `useUpdateUrl`.
 */
export function useSearchParams(): [URLSearchParams, SetSearchParams] {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const setSearchParams = useCallback<SetSearchParams>(
    (next) => {
      const current = new URLSearchParams(history.location.search);
      const before = current.toString();
      const resolved = typeof next === "function" ? next(current) : next;
      if (resolved.toString() === before) return;
      history.replace({ pathname: history.location.pathname, search: resolved.toString() });
    },
    [history],
  );

  return [searchParams, setSearchParams];
}
